<template>
  <div class="webhook-event-detail">
    <dl class="webhook-event-detail__grid">
      <div class="webhook-event-detail__item">
        <dt class="webhook-event-detail__label">ID</dt>
        <dd class="webhook-event-detail__value">{{ event.id }}</dd>
      </div>
      <div class="webhook-event-detail__item">
        <dt class="webhook-event-detail__label">Estado</dt>
        <dd class="webhook-event-detail__value">
          <span :class="['webhook-event-detail__status', `webhook-event-detail__status--${event.status}`]">
            {{ statusLabel(event.status) }}
          </span>
        </dd>
      </div>
      <div class="webhook-event-detail__item">
        <dt class="webhook-event-detail__label">Origen</dt>
        <dd class="webhook-event-detail__value">{{ event.source }}</dd>
      </div>
      <div class="webhook-event-detail__item">
        <dt class="webhook-event-detail__label">Evento</dt>
        <dd class="webhook-event-detail__value">{{ event.eventType || '—' }}</dd>
      </div>
      <div class="webhook-event-detail__item">
        <dt class="webhook-event-detail__label">Tienda</dt>
        <dd class="webhook-event-detail__value">
          {{ event.store ? `${event.store.name} (#${event.store.id})` : '—' }}
        </dd>
      </div>
      <div class="webhook-event-detail__item">
        <dt class="webhook-event-detail__label">ID de entrega</dt>
        <dd class="webhook-event-detail__value webhook-event-detail__value--mono">{{ event.externalId || '—' }}</dd>
      </div>
      <div class="webhook-event-detail__item">
        <dt class="webhook-event-detail__label">Disparado</dt>
        <dd class="webhook-event-detail__value">{{ formatDate(event.triggeredAt) }}</dd>
      </div>
      <div class="webhook-event-detail__item">
        <dt class="webhook-event-detail__label">Recibido</dt>
        <dd class="webhook-event-detail__value">{{ formatDate(event.createdAt) }}</dd>
      </div>
      <div class="webhook-event-detail__item">
        <dt class="webhook-event-detail__label">Procesado</dt>
        <dd class="webhook-event-detail__value">{{ formatDate(event.processedAt) }}</dd>
      </div>
      <div class="webhook-event-detail__item">
        <dt class="webhook-event-detail__label">Actualizado</dt>
        <dd class="webhook-event-detail__value">{{ formatDate(event.updatedAt) }}</dd>
      </div>
    </dl>

    <div v-if="event.errorMessage" class="webhook-event-detail__error">
      <span class="webhook-event-detail__label">Error</span>
      <p class="webhook-event-detail__error-text">{{ event.errorMessage }}</p>
    </div>

    <div class="webhook-event-detail__payload">
      <div class="webhook-event-detail__payload-head">
        <span class="webhook-event-detail__label">Payload</span>
        <AppTextButton
          :label="copied ? 'Copiado' : 'Copiar'"
          :icon="copied ? 'check' : 'content-copy'"
          @click="copyPayload" />
      </div>
      <pre class="webhook-event-detail__code"><code>{{ payloadText }}</code></pre>
    </div>
  </div>
</template>

<script>
import moment from 'moment-timezone'
import AppTextButton from '../UI/AppTextButton.vue'
import { formatWebhookPayload, webhookEventStatusLabel } from '@/utils/webhookEventUtils'

export default {
  name: 'WebhookEventDetail',
  components: { AppTextButton },
  props: {
    event: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      copied: false
    }
  },
  computed: {
    payloadText () {
      return formatWebhookPayload(this.event.payload)
    }
  },
  watch: {
    event () {
      this.copied = false
    }
  },
  methods: {
    statusLabel: webhookEventStatusLabel,
    formatDate (value) {
      return value ? moment(value).tz('America/Santiago').format('DD-MM-YYYY HH:mm:ss') : '—'
    },
    async copyPayload () {
      try {
        await navigator.clipboard.writeText(this.payloadText)
        this.copied = true
        setTimeout(() => { this.copied = false }, 2000)
      } catch (error) {
        this.$emit('copy-error', error)
      }
    }
  }
}
</script>

<style scoped>
.webhook-event-detail {
  display: flex;
  flex-direction: column;
  gap: var(--app-spacing-md);
}

.webhook-event-detail__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: var(--app-spacing-sm) var(--app-spacing-md);
  margin: 0;
}

.webhook-event-detail__item {
  min-width: 0;
}

.webhook-event-detail__label {
  display: block;
  font-size: var(--app-font-size-sm);
  font-weight: 600;
  color: var(--app-primary);
}

.webhook-event-detail__value {
  margin: 0;
  word-break: break-word;
}

.webhook-event-detail__value--mono {
  font-family: monospace;
  font-size: var(--app-font-size-sm);
}

.webhook-event-detail__status {
  display: inline-block;
  padding: 2px 10px;
  border-radius: var(--app-border-radius-lg);
  font-size: var(--app-font-size-sm);
  font-weight: 600;
  color: #fff;
  background-color: var(--app-icon-color);
}

.webhook-event-detail__status--processed {
  background-color: var(--app-success);
}

.webhook-event-detail__status--failed {
  background-color: var(--app-danger);
}

.webhook-event-detail__status--processing {
  background-color: var(--app-info);
}

.webhook-event-detail__status--received {
  background-color: var(--app-warning);
}

.webhook-event-detail__error {
  padding: var(--app-spacing-sm) var(--app-spacing-md);
  border-left: 4px solid var(--app-danger);
  background-color: var(--app-danger-focus);
  border-radius: var(--app-border-radius);
}

.webhook-event-detail__error-text {
  margin: 0;
  word-break: break-word;
}

.webhook-event-detail__payload-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--app-spacing-xs);
}

.webhook-event-detail__code {
  margin: 0;
  max-height: 50vh;
  overflow: auto;
  padding: var(--app-spacing-md);
  background-color: var(--app-primary);
  color: #e2e8f0;
  border-radius: var(--app-border-radius);
  font-size: 0.8rem;
  line-height: 1.4;
  white-space: pre;
}
</style>
